/**
 * DashboardMainContent Component
 * 대시보드 메인 콘텐츠 - 협력사별 수행현황
 */
import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getItem } from '@/shared/utils/storage';
import { dashboardApi, DashboardWeeklyResponse, DashboardWeeklySupplierPerformance } from '../../../api/dashboardApi';
import Loading from '@/shared/components/feedback/Loading';
import { supplierApi } from '@/features/partner/supplierStatus/api/SupplierApi';
import { Supplier, supplierListFromDto } from '@/features/partner/supplierStatus/types/supplier.types';
import WeekSelector from '@/shared/components/forms/WeekSelector';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';

// 부릉 하루 기준: 06:00 ~ 익일 05:59
const getDashboardDate = (): string => {
    const now = new Date();
    if (now.getHours() < 6) {
        now.setDate(now.getDate() - 1);
    }
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const SLOT_COLORS = [
    { headerBg: 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)', headerText: '#c2410c', rateText: '#ea580c' }, // 아침 - 주황
    { headerBg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', headerText: '#1e40af', rateText: '#2563eb' }, // 오후 - 파랑
    { headerBg: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)', headerText: '#5b21b6', rateText: '#7c3aed' }, // 저녁 - 보라
    { headerBg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', headerText: '#3730a3', rateText: '#4f46e5' }, // 심야 - 남색
    { headerBg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', headerText: '#15803d', rateText: '#16a34a' }, // 초록
    { headerBg: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', headerText: '#be185d', rateText: '#db2777' }, // 핑크
    { headerBg: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)', headerText: '#a16207', rateText: '#ca8a04' }, // 노랑
    { headerBg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', headerText: '#991b1b', rateText: '#dc2626' }, // 빨강
];

interface DashboardMainContentProps {
    userType: string;
}

const DashboardMainContent: React.FC<DashboardMainContentProps> = ({ userType }) => {
    const actualUserType = getItem<string>('USER_TYPE') || userType;
    const userId = getItem<string>('USER_ID') || '';
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>(() => {
        return actualUserType === 'SUPPLIER' ? userId : '';
    });
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [weeklyData, setWeeklyData] = useState<DashboardWeeklyResponse | null>(null);
    const [topAreaData, setTopAreaData] = useState<DashboardWeeklyResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'weekly' | 'realtime'>('realtime');
    const [selectedDate, setSelectedDate] = useState<string>(getDashboardDate);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [displayMode, setDisplayMode] = useState<'count' | 'rate'>('rate');
    const [slotCountFilter, setSlotCountFilter] = useState<string>('');
    const [acceptRateFilter, setAcceptRateFilter] = useState<string>('');
    const [hoveredCell, setHoveredCell] = useState<{ suppId: string; date: string } | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    type SortColumn = 'finishCnt' | 'acceptanceRate' | 'rejectCnt';
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const cycleSort = (col: SortColumn) => {
        if (sortColumn === col) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortColumn(null); setSortDir('asc'); }
        } else {
            setSortColumn(col);
            setSortDir('asc');
        }
    };

    const sortIcon = (col: SortColumn) => {
        if (sortColumn !== col) return 'fa-sort';
        return sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    };

    const sortSuppliers = (suppliers: DashboardWeeklySupplierPerformance[]) => {
        if (!sortColumn) return suppliers;
        const dateKey = selectedDate.replace(/-/g, '');
        const getSlotFinishCnt = (s: DashboardWeeklySupplierPerformance) =>
            (s.dailySlotCounts?.[dateKey] || []).reduce(
                (sum: number, c: string) => sum + (parseInt(c.split('/')[0]) || 0),
                0,
            );
        return [...suppliers].sort((a, b) => {
            let aVal: number, bVal: number;
            if (sortColumn === 'finishCnt') {
                // 화면에 표시되는 slotFinishCnt 기준으로 정렬
                aVal = getSlotFinishCnt(a);
                bVal = getSlotFinishCnt(b);
            }
            else if (sortColumn === 'acceptanceRate') { aVal = a.acceptanceRate ?? 0; bVal = b.acceptanceRate ?? 0; }
            else { aVal = (a.rejectCnt ?? 0) + (a.assignCancelCnt ?? 0) + (a.deliCancelCnt ?? 0); bVal = (b.rejectCnt ?? 0) + (b.assignCancelCnt ?? 0) + (b.deliCancelCnt ?? 0); }
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
    };

    const initializeDefaultDateRange = () => {
        const today = new Date();
        const format = (d: Date) => d.toISOString().split('T')[0];
        const currentDay = today.getDay();
        const diffToWed = (currentDay + 7 - 3) % 7;
        const wed = new Date(today);
        wed.setDate(today.getDate() - diffToWed);
        const tue = new Date(wed);
        tue.setDate(wed.getDate() + 6);

        // WeekSelector는 현재 주차를 기본값으로 표시
        setFromDate(format(wed));
        setToDate(format(tue));
    };

    // 토글 변경 시 필터 초기화
    const handleViewModeChange = (mode: 'weekly' | 'realtime') => {
        setViewMode(mode);
        if (actualUserType === 'HEADQUARTERS') {
            setSelectedSupplierId('');
        }
        setSlotCountFilter('');
        setAcceptRateFilter('');
        if (mode === 'realtime') {
            setSelectedDate(getDashboardDate());
        }
    };

    // 요약 통계 계산
    const getSummaryStats = () => {
        let activeSuppliers = 0;

        if (viewMode === 'weekly') {
            // 주별 모드 : 이번주(오늘 포함) 활동한 협력사의 전체 수 (완료 1건 이상)
            activeSuppliers = weeklyData?.activeSupplierCount || 0;
        } else {
            // 실시간/일별 모드: 오늘 완료 1건 이상 및 등록라이더 1명 이상의 협력사 수
            // topAreaData는 당일 데이터를 포함
            activeSuppliers =
                topAreaData?.supplierPerformances?.filter((s: DashboardWeeklySupplierPerformance) => (s.finishCnt || 0) >= 1 && (s.riderCount || 0) >= 1)
                    .length || 0;
        }

        // 총 배달 완료 건수
        let totalCompleted = 0;
        if (viewMode === 'weekly') {
            // 주별: 주간 완료한 오더 수의 총합
            totalCompleted = weeklyData?.totalFinishCnt || 0;
        } else {
            // 실시간/일별: 각 슬롯별 완료 건수의 합 (슬롯 수가 동적으로 다를 수 있으므로 파싱)
            const dateKey = selectedDate?.replace(/-/g, '') || '';
            totalCompleted =
                topAreaData?.supplierPerformances?.reduce(
                    (total: number, supplier: DashboardWeeklySupplierPerformance) => {
                        const slotCounts = supplier.dailySlotCounts?.[dateKey] || [];
                        return (
                            total +
                            slotCounts.reduce((sum: number, countStr: string) => {
                                return sum + (parseInt(countStr.split('/')[0]) || 0);
                            }, 0)
                        );
                    },
                    0,
                ) || 0;
        }

        // 수락률
        let acceptRate = 0;
        if (viewMode === 'weekly') {
            // 주별: 주간 수락률
            acceptRate = weeklyData?.periodAcceptanceRate || 0;
        } else {
            // 실시간/일별: 오늘 수락률
            acceptRate = topAreaData?.periodAcceptanceRate || 0;
        }

        // 슬롯 달성률
        let slotAchievementRate = 0;
        if (viewMode === 'weekly') {
            // 주별: 주간 슬롯 달성률
            slotAchievementRate = weeklyData?.periodSlotAchievementRate || 0;
        } else {
            // 실시간/일별: 오늘 슬롯 달성률
            slotAchievementRate = topAreaData?.periodSlotAchievementRate || 0;
        }

        return { activeSuppliers, totalCompleted, acceptRate, slotAchievementRate };
    };

    useEffect(() => {
        if (actualUserType !== 'HEADQUARTERS') {
            return;
        }

        const fetchSuppliers = async () => {
            try {
                const response = await supplierApi.getSupplierList();
                const supplierData = supplierListFromDto(response);
                setSuppliers(supplierData.suppliers);
                // 기본값은 빈 문자열 (전체 협력사 조회)로 유지
            } catch {
                // 에러는 조용히 무시 (대시보드 주요 기능은 아님)
            }
        };

        fetchSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]);

    useEffect(() => {
        initializeDefaultDateRange();
    }, []);

    const handleSearch = async () => {
        setLoading(true);
        try {
            const baseSuppId = actualUserType === 'HEADQUARTERS' ? selectedSupplierId || undefined : userId;

            // 상단 영역도 선택된 주간 데이터로 변경
            const res = await dashboardApi.getWeekly({ fromDate, toDate, suppId: baseSuppId });
            setTopAreaData(res);
            setWeeklyData(res);
        } catch {
            // 조회 실패 시 조용히 무시
        } finally {
            setLoading(false);
        }
    };

    // 초기 데이터 조회: 일별 데이터와 주간 데이터
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!fromDate || !toDate) return;

            setLoading(true);
            try {
                const baseSuppId = actualUserType === 'HEADQUARTERS' ? selectedSupplierId || undefined : userId;

                // 일별 데이터: selectedDate 기준 (실시간/일별 모드용)
                const topRes = await dashboardApi.getWeekly({
                    fromDate: selectedDate,
                    toDate: selectedDate,
                    suppId: baseSuppId,
                });
                setTopAreaData(topRes);

                // 주간 데이터 (fromDate, toDate 사용)
                const weeklyRes = await dashboardApi.getWeekly({
                    fromDate,
                    toDate,
                    suppId: baseSuppId,
                });
                setWeeklyData(weeklyRes);
            } catch {
                // 조회 실패 시 조용히 무시
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, selectedDate, actualUserType === 'HEADQUARTERS' ? selectedSupplierId : '']);

    const formatNumber = (value?: number) => {
        if (value === undefined || value === null) return '-';
        return value.toLocaleString();
    };

    // 요일만 반환 (YYYYMMDD -> 요일)
    const getWeekdayOnly = (yyyymmdd: string): string => {
        if (!yyyymmdd || yyyymmdd.length !== 8) return '';
        const year = Number(yyyymmdd.slice(0, 4));
        const month = Number(yyyymmdd.slice(4, 6)) - 1;
        const day = Number(yyyymmdd.slice(6, 8));
        const date = new Date(year, month, day);
        const labels = ['일', '월', '화', '수', '목', '금', '토'];
        return labels[date.getDay()];
    };

    // 주차 범위의 모든 날짜 생성 (YYYYMMDD 형식)
    const generateWeekDates = (startDate: string, endDate: string): string[] => {
        if (!startDate || !endDate) return [];
        const dates: string[] = [];
        const start = new Date(
            Number(startDate.slice(0, 4)),
            Number(startDate.slice(4, 6)) - 1,
            Number(startDate.slice(6, 8))
        );
        const end = new Date(Number(endDate.slice(0, 4)), Number(endDate.slice(4, 6)) - 1, Number(endDate.slice(6, 8)));

        const current = new Date(start);
        while (current <= end) {
            const year = current.getFullYear();
            const month = String(current.getMonth() + 1).padStart(2, '0');
            const day = String(current.getDate()).padStart(2, '0');
            dates.push(`${year}${month}${day}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    // 협력사 필터링 (달성그룹 필터 적용)
    const getFilteredSuppliers = () => {
        // viewMode에 따라 다른 데이터 소스 사용
        const dataSource = viewMode === 'weekly' ? weeklyData : topAreaData;
        let filtered = dataSource?.supplierPerformances || [];

        // 슬롯 개수 필터
        if (slotCountFilter && !isNaN(Number(slotCountFilter))) {
            const minSlots = Number(slotCountFilter);
            filtered = filtered.filter((s: DashboardWeeklySupplierPerformance) => {
                // 달성한 슬롯 수 계산 (간단히 finishCnt 기준으로)
                return (s.finishCnt || 0) >= minSlots;
            });
        }

        // 수락률 필터
        if (acceptRateFilter && !isNaN(Number(acceptRateFilter))) {
            const minRate = Number(acceptRateFilter);
            filtered = filtered.filter((s: DashboardWeeklySupplierPerformance) => (s.acceptanceRate || 0) >= minRate);
        }

        return filtered;
    };

    // 현재 viewMode에 따른 데이터 소스 가져오기
    const getCurrentDataSource = () => {
        return viewMode === 'weekly' ? weeklyData : topAreaData;
    };

    const DEFAULT_SLOT_NAMES = ['아침점심피크', '오후논피크', '저녁피크', '심야논피크'];

    // 슬롯별 달성 정보 가져오기 (툴팁용) - 슬롯 수 동적 지원
    const getSlotTooltipInfo = (suppId: string, date: string) => {
        const dataSource = getCurrentDataSource();
        const supplier = dataSource?.supplierPerformances?.find((s: DashboardWeeklySupplierPerformance) => s.suppId === suppId);
        if (!supplier || !supplier.dailySlots) return null;

        const slots = supplier.dailySlots[date];
        const slotCounts = supplier.dailySlotCounts?.[date];
        if (!slots || slots.length === 0) return null;

        const parseRate = (rateStr: string) => parseInt(rateStr.replace('%', '')) || 0;
        const isNa = (v: string) => v === '-';
        // target=0인 슬롯(아침/심야 등)은 '/' 없이 건수만 반환됨
        const hasTarget = (c: string) => c.includes('/');

        const dateSlotNames = supplier.dailySlotNames?.[date] ?? supplier.slotNames;
        return slots.map((slotRate: string, i: number) => {
            const count = slotCounts?.[i] || '-';
            const noTarget = !isNa(count) && !hasTarget(count);
            return {
                label: dateSlotNames?.[i] || `슬롯${i + 1}`,
                rate: slotRate,
                count,
                isAchieved: !isNa(slotRate) && !noTarget && parseRate(slotRate) >= 100,
                isNa: isNa(slotRate),
                isNoTarget: noTarget,
            };
        });
    };

    if (loading) {
        return <Loading />;
    }

    const summaryStats = getSummaryStats();

    return (
        <div className="dashboard-main-content dashboard-weekly-layout">
            {/* 1. 페이지 헤더 + 토글 버튼 + 날짜 선택 */}
            <div className="page-header dashboard-header-row">
                <h1 className="page-title">협력사별 수행현황</h1>
                <div className="header-controls">
                    {/* 토글 버튼 */}
                    <div className="view-mode-toggle">
                        <button
                            type="button"
                            className={`toggle-btn ${viewMode === 'realtime' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('realtime')}
                        >
                            실시간/일별
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('weekly')}
                        >
                            주별
                        </button>
                    </div>
                    {/* 날짜 선택 - 실시간/일별 모드에서만 표시 */}
                    {viewMode === 'realtime' && (
                        <div className="date-selector">
                            <i className="fas fa-calendar-alt" />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 2. 요약 통계 카드 4개 */}
            <div className="summary-stats-grid">
                <div className="summary-stat-card">
                    <div className="summary-stat-icon">
                        <i className="fas fa-building" />
                    </div>
                    <div className="summary-stat-content">
                        <div className="summary-stat-label">총 활성 협력사 수</div>
                        <div className="summary-stat-value">{formatNumber(summaryStats.activeSuppliers)}</div>
                    </div>
                </div>
                <div className="summary-stat-card">
                    <div className="summary-stat-icon">
                        <i className="fas fa-check-circle" />
                    </div>
                    <div className="summary-stat-content">
                        <div className="summary-stat-label">총 배달 완료 건수</div>
                        <div className="summary-stat-value">{formatNumber(summaryStats.totalCompleted)}</div>
                    </div>
                </div>
                <div className="summary-stat-card">
                    <div className="summary-stat-icon">
                        <i className="fas fa-percentage" />
                    </div>
                    <div className="summary-stat-content">
                        <div className="summary-stat-label">수락률</div>
                        <div className="summary-stat-value">{summaryStats.acceptRate.toFixed(1)}%</div>
                    </div>
                </div>
                <div className="summary-stat-card">
                    <div className="summary-stat-icon">
                        <i className="fas fa-chart-pie" />
                    </div>
                    <div className="summary-stat-content">
                        <div className="summary-stat-label">슬롯 달성률</div>
                        <div className="summary-stat-value">{summaryStats.slotAchievementRate.toFixed(1)}%</div>
                    </div>
                </div>
            </div>

            {/* 3. 협력사 필터 조건 */}
            <div className="card search-card">
                <div className="card-header filter-header" onClick={() => setIsFilterOpen(!isFilterOpen)}>
                    <span className="filter-title">
                        <i className="fas fa-filter" />
                        협력사 필터 조건
                    </span>
                    <i className={`fas fa-chevron-${isFilterOpen ? 'up' : 'down'} filter-toggle-icon`} />
                </div>
                {isFilterOpen && (
                    <div className="card-body">
                        <div className={`search-form dashboard-search-form ${actualUserType === 'HEADQUARTERS' ? '' : 'supplier-view'}`}>
                            {/* 협력사 선택 - 본사만 표시 */}
                            {actualUserType === 'HEADQUARTERS' && (
                                <div className="form-group">
                                    <div className="form-field">
                                        <label className="form-label">
                                            <i className="fas fa-building" />
                                            협력사
                                        </label>
                                        <SearchableSelect
                                            options={suppliers.map(
                                                (supplier: Supplier): SelectOption => ({
                                                    value: supplier.id,
                                                    label: supplier.name,
                                                })
                                            )}
                                            value={selectedSupplierId}
                                            onChange={setSelectedSupplierId}
                                            placeholder="협력사 검색..."
                                            showAllOption={true}
                                            allOptionLabel="전체"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 주차 선택 - 주별 모드에서만 표시 */}
                            {viewMode === 'weekly' && (
                                <div className="form-group">
                                    <div className="form-field">
                                        <label className="form-label">
                                            <i className="fas fa-calendar-week" />
                                            조회 기간
                                        </label>
                                        <WeekSelector
                                            value={{ startDate: fromDate, endDate: toDate }}
                                            onChange={async (startDate, endDate) => {
                                                setFromDate(startDate);
                                                setToDate(endDate);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 슬롯 달성 건수 필터 */}
                            <div className="form-group">
                                <div className="form-field">
                                    <label className="form-label">
                                        <i className="fas fa-check-circle" />
                                        슬롯 달성 건수 이상
                                    </label>
                                    <input
                                        type="number"
                                        className="text-input"
                                        placeholder="건수 입력..."
                                        value={slotCountFilter}
                                        onChange={(e) => setSlotCountFilter(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        min="0"
                                    />
                                </div>
                            </div>

                            {/* 수락률 필터 */}
                            <div className="form-group">
                                <div className="form-field">
                                    <label className="form-label">
                                        <i className="fas fa-percentage" />
                                        수락률 이상 (%)
                                    </label>
                                    <input
                                        type="number"
                                        className="text-input"
                                        placeholder="% 입력..."
                                        value={acceptRateFilter}
                                        onChange={(e) => setAcceptRateFilter(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            </div>

                            {/* 버튼 그룹 */}
                            <div className="form-group button-group">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        if (actualUserType === 'HEADQUARTERS') {
                                            setSelectedSupplierId('');
                                        }
                                        setSlotCountFilter('');
                                        setAcceptRateFilter('');
                                    }}
                                >
                                    <i className="fas fa-redo" />
                                    초기화
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleSearch}>
                                    <i className="fas fa-search" />
                                    검색
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 4. 협력사별 수행 현황 sub-title + 토글 */}
            <div className="supplier-performance-header">
                <h2 className="sub-title">협력사별 수행 현황</h2>
                {viewMode === 'weekly' && (
                    <div className="display-mode-toggle">
                        <button
                            type="button"
                            className={`toggle-btn ${displayMode === 'count' ? 'active' : ''}`}
                            onClick={() => setDisplayMode('count')}
                        >
                            건수
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${displayMode === 'rate' ? 'active' : ''}`}
                            onClick={() => setDisplayMode('rate')}
                        >
                            수행률
                        </button>
                    </div>
                )}
            </div>

            {/* 5. 협력사별 수행 현황 표 */}
            <div className="card supplier-performance-card">
                <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
                    {viewMode === 'weekly' ? (
                        /* 주별 모드: 기존 테이블 */
                        <table className="table dashboard-weekly-table supplier-performance-table">
                            <thead>
                                <tr>
                                    <th className="col-partner-name">협력사명</th>
                                    <th className="col-slot-set">세트</th>
                                    <th className="col-completed">완료 건수</th>
                                    <th className="col-accept-rate">수락률</th>
                                    {getCurrentDataSource() &&
                                        generateWeekDates(
                                            getCurrentDataSource()?.weekStartDate || '',
                                            getCurrentDataSource()?.weekEndDate || ''
                                        ).map((date) => (
                                            <th key={date} className="col-day">
                                                {getWeekdayOnly(date)}
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {getFilteredSuppliers().length > 0 ? (
                                    getFilteredSuppliers().map((supplier: DashboardWeeklySupplierPerformance, index: number) => {
                                        const currentData = getCurrentDataSource();
                                        const dates = generateWeekDates(
                                            currentData?.weekStartDate || '',
                                            currentData?.weekEndDate || ''
                                        );
                                        return (
                                            <tr
                                                key={supplier.suppId}
                                                className={index % 2 === 0 ? 'row-even' : 'row-odd'}
                                            >
                                                <td className="col-partner-name">{supplier.suppName}</td>
                                                <td className="col-slot-set">{supplier.slotSetCnt ?? '-'}</td>
                                                <td className="col-completed">{formatNumber(supplier.finishCnt)}</td>
                                                <td className="col-accept-rate">
                                                    {(supplier.acceptanceRate ?? 0).toFixed(1)}%
                                                </td>
                                                {dates.map((date) => {
                                                    const slots = supplier.dailySlots?.[date];
                                                    const slotCounts = supplier.dailySlotCounts?.[date];
                                                    const parseRate = (rateStr: string) =>
                                                        parseInt(rateStr?.replace('%', '')) || 0;

                                                    let dayRate = 0;
                                                    const missionRate = supplier.dailyMissionRate?.[date];
                                                    if (missionRate !== undefined) {
                                                        dayRate = parseRate(missionRate);
                                                    } else if (slots && slots.length > 0) {
                                                        const validSlots = slots.filter((s: string) => s !== '-');
                                                        const achievedCount = validSlots.filter(
                                                            (s: string) => parseRate(s) >= 100
                                                        ).length;
                                                        dayRate = validSlots.length > 0
                                                            ? (achievedCount / validSlots.length) * 100
                                                            : 0;
                                                    }

                                                    let totalCount = 0;
                                                    let totalTarget = 0;
                                                    if (slotCounts && slotCounts.length > 0) {
                                                        slotCounts.forEach((countStr: string) => {
                                                            if (countStr === '-') return;
                                                            const [count, target] = countStr.split('/').map(Number);
                                                            totalCount += count || 0;
                                                            totalTarget += target || 0;
                                                        });
                                                    }

                                                    return (
                                                        <td
                                                            key={date}
                                                            className={`col-day-cell ${dayRate >= 100 ? 'achieved' : dayRate > 0 ? 'partial' : ''}`}
                                                            onMouseEnter={(e) => {
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                setTooltipPosition({
                                                                    top: rect.top - 10,
                                                                    left: rect.left + rect.width / 2,
                                                                });
                                                                setHoveredCell({ suppId: supplier.suppId, date });
                                                            }}
                                                            onMouseLeave={() => {
                                                                setHoveredCell(null);
                                                                setTooltipPosition(null);
                                                            }}
                                                        >
                                                            {displayMode === 'rate'
                                                                ? `${dayRate.toFixed(0)}%`
                                                                : `${totalCount}/${totalTarget}`}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="empty-message">
                                            협력사별 수행 현황 데이터가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (() => {
                        /* 실시간/일별 모드: 단일 통합 테이블 - 시간대별 달성 컬럼 */
                        const currentDate = selectedDate.replace(/-/g, '');
                        const realtimeSuppliers = getFilteredSuppliers();

                        if (realtimeSuppliers.length === 0) {
                            return (
                                <table className="table dashboard-weekly-table supplier-realtime-table">
                                    <tbody>
                                    <tr>
                                        <td colSpan={6} className="empty-message">
                                            협력사별 수행 현황 데이터가 없습니다.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            );
                        }

                        return (
                            <table className="table dashboard-weekly-table supplier-realtime-table">
                                <thead>
                                <tr>
                                    <th>협력사명</th>
                                    <th>세트</th>
                                    <th
                                        onClick={() => cycleSort('finishCnt')}
                                        style={{ cursor: 'pointer',  userSelect: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        총 완료 건수{' '}
                                        <i
                                            className={`fas fa-fw ${sortIcon('finishCnt')}`}
                                            style={{ opacity: sortColumn === 'finishCnt' ? 1 : 0.4 }}
                                        />
                                    </th>
                                    <th
                                        onClick={() => cycleSort('acceptanceRate')}
                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        수락률{' '}
                                        <i
                                            className={`fas fa-fw ${sortIcon('acceptanceRate')}`}
                                            style={{ opacity: sortColumn === 'acceptanceRate' ? 1 : 0.4 }}
                                        />
                                    </th>
                                    <th
                                        onClick={() => cycleSort('rejectCnt')}
                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        거절/취소{' '}
                                        <i
                                            className={`fas fa-fw ${sortIcon('rejectCnt')}`}
                                            style={{ opacity: sortColumn === 'rejectCnt' ? 1 : 0.4 }}
                                        />
                                    </th>
                                    <th className="col-slot-achievement">시간대별 달성</th>
                                </tr>
                                </thead>
                                <tbody>
                                {sortSuppliers(realtimeSuppliers).map(
                                    (supplier: DashboardWeeklySupplierPerformance, index: number) => {
                                        const slots = supplier.dailySlots?.[currentDate] || [];
                                        const slotCounts =
                                            supplier.dailySlotCounts?.[currentDate] || [];
                                        const slotFinishCnt = slotCounts.reduce(
                                            (sum: number, countStr: string) =>
                                                sum + (parseInt(countStr.split('/')[0]) || 0),
                                            0,
                                        );
                                        const slotNames =
                                            supplier.dailySlotNames?.[currentDate] ??
                                            (supplier.slotNames && supplier.slotNames.length > 0
                                                ? supplier.slotNames
                                                : DEFAULT_SLOT_NAMES);
                                        const rejectCnt = (supplier.rejectCnt ?? 0) + (supplier.assignCancelCnt ?? 0) + (supplier.deliCancelCnt ?? 0);
                                        return (
                                            <tr
                                                key={supplier.suppId}
                                                className={index % 2 === 0 ? 'row-even' : 'row-odd'}
                                            >
                                                <td>{supplier.suppName}</td>
                                                <td>{supplier.slotSetCnt ?? '-'}</td>
                                                <td>{formatNumber(slotFinishCnt)}</td>
                                                <td>{(supplier.acceptanceRate ?? 0).toFixed(1)}%</td>
                                                <td>{formatNumber(rejectCnt)}</td>
                                                <td className="col-slot-achievement-cell">
                                                    <div className="slot-achievement-inline">
                                                        {slotNames.map((name, i) => {
                                                            const color =
                                                                SLOT_COLORS[i % SLOT_COLORS.length];
                                                            const isNa =
                                                                slotCounts[i] === '-' ||
                                                                slots[i] === '-';
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className="slot-achievement-item"
                                                                >
                                                                        <span
                                                                            className="slot-achievement-label"
                                                                            style={{ color: color.headerText }}
                                                                        >
                                                                            {name}
                                                                        </span>
                                                                        {(supplier.dailySlotTimeRanges?.[currentDate]?.[i] ?? supplier.slotTimeRanges?.[i]) && (
                                                                            <span className="slot-achievement-time">
                                                                                {supplier.dailySlotTimeRanges?.[currentDate]?.[i] ?? supplier.slotTimeRanges![i]}
                                                                            </span>
                                                                        )}
                                                                        {isNa ? (
                                                                            <span className="slot-na">—</span>
                                                                        ) : (
                                                                            <>
                                                                                <span className="slot-achievement-count">
                                                                                    {slotCounts[i] || '0/0'}
                                                                                </span>
                                                                                <span
                                                                                    className="slot-achievement-rate"
                                                                                    style={{ color: color.rateText }}
                                                                                >
                                                                                    {slots[i] || '0%'}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                                </tbody>
                            </table>
                        );
                    })()}
                </div>
            </div>

            {/* Portal로 렌더링되는 툴팁 (DOM 계층 밖에서 렌더링) */}
            {hoveredCell &&
                tooltipPosition &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        className="slot-tooltip-portal"
                        style={{
                            position: 'fixed',
                            top: tooltipPosition.top,
                            left: tooltipPosition.left,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 99999,
                        }}
                    >
                        {(() => {
                            const tooltipItems = getSlotTooltipInfo(hoveredCell.suppId, hoveredCell.date);
                            if (!tooltipItems) return <div className="tooltip-no-data">데이터 없음</div>;
                            return (
                                <>
                                    {tooltipItems.map((slot: {label: string; isNa: boolean; isAchieved: boolean; isNoTarget: boolean; count: string; rate: string}) => (
                                        <div
                                            key={slot.label}
                                            className={`tooltip-item ${slot.isNa ? 'slot-na-item' : slot.isNoTarget ? 'no-target' : slot.isAchieved ? 'achieved' : 'not-achieved'}`}
                                        >
                                            {slot.label}: {slot.isNa ? '비해당' : slot.isNoTarget ? `${slot.count}건` : `${slot.count} (${slot.rate})`}
                                        </div>
                                    ))}
                                </>
                            );
                        })()}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default DashboardMainContent;
